<script setup>
const pollingTimeout = ref(null);
const POLLING_INTERVAL_MS = 3000; // Polling interval in milliseconds
const POLLING_TIMEOUT_MS = 40 * 60 * 1000; // Timeout after 40 minutes

const LOADING_MESSAGES = Object.freeze({
  default: 'Please wait; do not refresh or close the page.',
  processing:
    'Almost there! Your request is being processed. Please do not refresh or close the page.',
  connectionLost: 'Connection lost. Please refresh the page to continue.',
  subscriptionRejected: 'Something went wrong.',
  resumingInquiryError:
    'Something went wrong. Please refresh the page and try again.',
});

import { useStore } from 'vuex';
import { useRouter } from 'vue-router';
import { usePersona } from '@/composables/usePersona';
import LoadingIDVModal from '@/components/modals/LoadingIDVModal.vue';
import { ref, computed, onMounted, onUnmounted, watchEffect } from 'vue';

const loadingMessage = ref(LOADING_MESSAGES.default);
const store = useStore();
const router = useRouter();
const persona = usePersona();

const pollingInterval = ref(null);

const carrierId = computed(
  //Including the verification of the carrier ID within the company. This case is specifically
  // necessary for the Inbound flow since the requested_carrier_id is null.
  () =>
    store.state.users.currentUser?.requested_carrier_id ||
    store.state.users.currentUser?.company?.carrier_id
);

const underReviewMsg =
  'Your identity verification is under review. </br> Please reach out to <a href="mailto:security@highway.com" class="text-yellow-500 underline">security@highway.com</a>.';

const enforcedUnderReview = computed(
  () =>
    store.state.users.featureFlags?.display_under_review_idv_message?.enabled
);
watchEffect(() => {
  if (enforcedUnderReview.value) {
    loadingMessage.value = underReviewMsg;
  }
});

const latestInquiry = computed(() => store.getters['users/getLatestInquiry']);

onMounted(async () => {
  try {
    console.log('🎆 Mount IDV flow');
    await store.dispatch(
      'identityVerification/getCustomFields',
      carrierId.value
    );
    await setupPersonaClient();
  } catch (error) {
    console.error('Failed to set up Persona/ActionCable:', error);
  }
});
/**
 * Sets up the Persona client depending on whether we need to create a new inquiry
 * or resume an existing one.
 */
const setupPersonaClient = async () => {
  const inquiry = store.getters['users/getLatestInquiry'];

  // Once an inquiry is approved, we update the user's record with the timestamp in last_verified_identity_at.
  // If a user is approved, we don't need to send them through the IDV flow again.
  const isUserIdentityVerified =
    store.state.currentUser?.last_verified_identity_at;

  if (inquiry?.status === 'approved' || isUserIdentityVerified) {
    console.log('✅ Inquiry is either approved or declined, no action needed');
    return;
  }
  const getInquiry = await store.dispatch('users/getInquiry');

  persona.initializePersonaClientWithInquiry(
    getInquiry?.data?.external_id,
    getInquiry?.data?.session_token
  );

  startPolling();
};

const startPolling = () => {
  console.log('🔄 Starting polling for inquiries...');

  pollingTimeout.value = setTimeout(() => {
    console.log('⏳ Polling timed out, stopping polling...');
    stopPolling();
    loadingMessage.value = LOADING_MESSAGES.resumingInquiryError;
  }, POLLING_TIMEOUT_MS);

  pollingInterval.value = setInterval(async () => {
    console.log('🔄 Polling inquiries...');
    await store.dispatch(
      'users/pollInquiries',
      store.state.users.currentUser.id
    );
    const latestStatus = latestInquiry.value?.status;

    if (['approved', 'declined', 'under_review'].includes(latestStatus)) {
      stopPolling();
      handleInquiryStatus(latestStatus);
    }
  }, POLLING_INTERVAL_MS);
};

const stopPolling = () => {
  if (pollingInterval.value) {
    clearInterval(pollingInterval.value);
    pollingInterval.value = null;
  }
  if (pollingTimeout.value) {
    clearTimeout(pollingTimeout.value);
    pollingTimeout.value = null;
  }
};

onUnmounted(() => {
  stopPolling();
});

const handleInquiryStatus = async (status) => {
  console.log('🔍 Handling inquiry status:', status);

  if (status === 'declined' || status === 'under_review') {
    return router.push({ name: 'IDVFailedVerification' });
  }
};
</script>

<template>
  <section>
    <LoadingIDVModal :loading-message="loadingMessage" />
  </section>
</template>
<style scoped>
@keyframes blink {
  0% {
    opacity: 1;
  }

  33% {
    opacity: 0;
  }

  66% {
    opacity: 1;
  }
}

.dots::after {
  content: '.';
  animation: blink 1s steps(5, end) infinite;
}
</style>
